'use client';

import React, { forwardRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import { ICONS } from '@/constants/constants';

interface DropdownOption {
    value: string;
    label: string;
}

interface DropdownProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label: string;
    options: DropdownOption[];
    error?: string;
    className?: string;
    labelClassName?: string;
    placeholder?: string;
}

const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
    ({ label, options, error, className = '', labelClassName = '', placeholder = 'Select an option', ...props }, ref) => {
        return (
            <div className="w-full font-sans font-normal">
                <label className={`block text-sm font-medium text-gray-800 mb-2 ${labelClassName}`}>
                    {label}
                </label>
                <div className="relative">
                    <select
                        ref={ref}
                        className={`
              w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-transparent
              transition-all duration-200 outline-none appearance-none
              ${error ? 'border-red-500 focus:ring-red-500' : ''}
              ${className}
            `}
                        {...props}
                    >
                        {placeholder && (
                            <option value="" disabled>
                                {placeholder}
                            </option>
                        )}
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {/* Custom Dropdown Arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <Image src={ICONS.downarrow} alt='drop' height={13} width={13} />
                    </div>

                    {/* Error Icon */}
                    {error && (
                        <ExclamationCircleIcon
                            className="h-5 w-5 text-red-500 absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none"
                        />
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <p className="mt-1 text-sm text-red-600">{error}</p>
                )}
            </div>
        );
    }
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;